import { fromZonedTime } from 'date-fns-tz';
import { parse } from 'date-fns';
import { TimeZone, timeZoneMapping } from './timeZone/enum';
import { validateDate } from './dateFormat/formatDateTime';

import * as Sentry from '@sentry/react';

const referenceDate = new Date(2000, 0, 1);

/**
 * Try to parse a date/time string with date-fns using the given format.
 * Returns a valid Date or null.
 */
const tryParse = (str: string, format: string): Date | null => {
  try {
    const d = parse(str, format, referenceDate);
    return isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
};

/**
 * Parse a date/time string, optionally with a trailing IANA timezone,
 * using the given date-fns format token for the date/time portion.
 * Returns a UTC Date or null.
 */
const parseWithOptionalTimezone = (
  dateString: string,
  format: string,
  timeZone?: string,
): Date | null => {
  // Try to extract an IANA timezone from the end of the string
  // IANA names look like "America/New_York", "Europe/Berlin", "UTC", etc.
  const tzMatch = dateString.match(/\s+([\w+\-/]+)$/);

  if (tzMatch) {
    const tz = tzMatch[1];
    const dateTimePart = dateString.slice(0, -tzMatch[0].length);
    const parsed = tryParse(dateTimePart, format);
    if (parsed) {
      return fromZonedTime(parsed, tz);
    }
  }

  if (timeZone) {
    // Append supplied timezone and try again
    const withTz = `${dateString} ${timeZone}`;
    const tzMatch2 = withTz.match(/\s+([\w+\-/]+)$/);
    if (tzMatch2) {
      const tz = tzMatch2[1];
      const dateTimePart = withTz.slice(0, -tzMatch2[0].length);
      const parsed = tryParse(dateTimePart, format);
      if (parsed) {
        return fromZonedTime(parsed, tz);
      }
    }
  }

  // No timezone — treat as UTC
  const parsed = tryParse(dateString, format);
  if (parsed) {
    return fromZonedTime(parsed, 'UTC');
  }

  return null;
};

function parseHumanDateString(dateString: string, timeZone?: string) {
  // Early return for null/undefined/empty strings - no Sentry capture needed
  if (!dateString || dateString.trim() === '') {
    return null;
  }

  // Trim whitespace from the input and normalize internal spaces
  dateString = dateString.trim().replace(/\s+/g, ' ');

  // Early return for strings that don't contain any numbers - likely not date strings
  // This prevents excessive Sentry noise from parsing non-date strings
  if (!/\d/.test(dateString)) {
    return null;
  }

  // Handle numeric timezone offsets like +04, +0400, -05:30, etc.
  const numericTimezoneRegex = /([+-]\d{2}(?::?\d{2})?)\s*$/;
  const numericTimezoneMatch = dateString.match(numericTimezoneRegex);

  if (numericTimezoneMatch) {
    const offset = numericTimezoneMatch[1];
    // Normalize the offset format for ISO string compatibility
    let normalizedOffset = offset;

    // If it's just +04 or -05, add :00
    if (/^[+-]\d{2}$/.test(offset)) {
      normalizedOffset = offset + ':00';
    }
    // If it's +0400 format, add colon
    else if (/^[+-]\d{4}$/.test(offset)) {
      normalizedOffset = offset.slice(0, 3) + ':' + offset.slice(3);
    }

    // Remove the timezone from the original string and try to parse as ISO
    const baseDateString = dateString.replace(numericTimezoneRegex, '').trim();

    // Try to create an ISO-compatible string
    if (/^\d{4}-\d{2}-\d{2}/.test(baseDateString)) {
      let isoString = baseDateString;

      // Add time if missing
      if (!/T\d{2}:\d{2}/.test(isoString)) {
        if (/ \d{2}:\d{2}/.test(isoString)) {
          isoString = isoString.replace(' ', 'T');
        } else {
          isoString += 'T00:00:00';
        }
      }

      // Add seconds if missing
      if (!/:\d{2}:\d{2}/.test(isoString)) {
        isoString += ':00';
      }

      // Add the normalized timezone offset
      isoString += normalizedOffset;

      try {
        const date = new Date(isoString);
        if (!isNaN(date.getTime())) {
          return date;
        }
      } catch {
        // Fall through to other parsing methods
      }
    }
  }

  for (const [key, value] of Object.entries(timeZoneMapping)) {
    // Skip UTC mapping — fromZonedTime handles 'UTC' natively
    // The 'Z' replacement is only useful for ISO formats, which are handled elsewhere
    if (key === 'UTC') {
      continue;
    }
    if (dateString.includes(key)) {
      dateString = dateString.replace(key, value);
      break;
    }
  }

  // First try to parse as simple ISO date format (YYYY-MM-DD)
  try {
    // Check for simple date format like "2022-07-01"
    const simpleDateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (simpleDateRegex.test(dateString)) {
      const date = new Date(`${dateString}T00:00:00Z`);
      if (!isNaN(date.getTime())) {
        return date;
      }
    }

    // Check for YYYY-MM-DD HH:mm:ss format without timezone
    const datetimeRegex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;
    if (datetimeRegex.test(dateString)) {
      // Convert to ISO format and parse as UTC
      const isoString = dateString.replace(' ', 'T') + 'Z';
      const date = new Date(isoString);
      if (!isNaN(date.getTime())) {
        return date;
      }
    }

    // Check for YYYY-MM-DD HH:mm format without timezone
    const datetimeNoSecondsRegex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/;
    if (datetimeNoSecondsRegex.test(dateString)) {
      // Convert to ISO format and parse as UTC
      const isoString = dateString.replace(' ', 'T') + ':00Z';
      const date = new Date(isoString);
      if (!isNaN(date.getTime())) {
        return date;
      }
    }

    // Try human-readable formats: "dd MMM yyyy HH:mm:ss [tz]" and "dd MMM yyyy HH:mm [tz]"
    // Also handle numeric month formats: "dd-MM-yyyy HH:mm:ss [tz]" and "dd-MM-yyyy HH:mm [tz]"
    const formats = [
      'dd MMM yyyy HH:mm:ss',
      'dd MMM yyyy HH:mm',
      'dd-MM-yyyy HH:mm:ss',
      'dd-MM-yyyy HH:mm',
    ];
    for (const format of formats) {
      const result = parseWithOptionalTimezone(dateString, format, timeZone);
      if (result) {
        return result;
      }
    }

    throw new Error(`Invalid date format for "${dateString}"`);
  } catch (error) {
    console.error('Error parsing date string:', error, dateString);

    // Only capture exceptions for strings that look like they could be dates
    // and are not null/undefined (additional safety check)
    // Also ensure dateString is actually a string before calling .trim()
    if (
      dateString &&
      typeof dateString === 'string' &&
      dateString.trim() !== ''
    ) {
      Sentry.captureException(error, {
        tags: {
          function: 'parseHumanDateString',
          category: 'date_parsing_exception',
        },
        extra: {
          originalDateString: dateString,
          timeZone: timeZone,
        },
        level: 'error',
        fingerprint: ['parseHumanDateString', dateString], // Group similar errors together
      });
    }

    return null;
  }
}

function isISODateString(dateString: string) {
  const isoRegex =
    /^\d{4}-\d{2}-\d{2}(?:[T\s]\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})?)?$/;
  return isoRegex.test(dateString);
}

export function parseDate(
  input: string | number | null | undefined,
  timeZone?: string,
) {
  // Early return for null/undefined
  if (input === null || input === undefined) {
    return null;
  }

  // Guard against non-string/non-number types at runtime (callers may pass `any`)
  if (typeof input !== 'string' && typeof input !== 'number') {
    return null;
  }

  // Early return for empty or 'calculating' strings to avoid Sentry noise
  if (
    input === '' ||
    (typeof input === 'string' && input.toLowerCase().includes('calculating'))
  ) {
    return null;
  }

  // Early return for PHP-style date format strings (e.g. "Day 7 d M Y 07:26 UTC")
  // These are format templates, not actual dates, and should be ignored silently.
  if (typeof input === 'string' && /\bd\s+M\s+Y\b/.test(input)) {
    return null;
  }

  // Normalize whitespace for string inputs
  let dateString: string | number =
    typeof input === 'string' ? input.trim().replace(/\s+/g, ' ') : input;

  let date;

  if (typeof dateString === 'number' || /^\d+(\.\d+)?$/.test(dateString)) {
    let timestamp = parseFloat(dateString.toString());
    if (dateString.toString().split('.')[0].length === 10) {
      timestamp *= 1000; // Convert seconds to milliseconds if it's a 10-digit number
    }
    date = new Date(timestamp);
  }
  // Check for ISO 8601 format
  else if (isISODateString(dateString)) {
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      date = new Date(`${dateString}T00:00:00Z`);
    } else if (dateString.endsWith('Z')) {
      // IMPORTANT: When a date string already has the 'Z' indicator (e.g., "2025-05-02T11:50:39.654210Z"),
      // it explicitly indicates that the time is already in UTC/Zulu time
      // Using fromZonedTime() on a Z-terminated string can cause incorrect double-conversion
      // Instead, we use the native Date constructor which correctly interprets the Z indicator
      date = new Date(dateString);
    } else {
      // IMPORTANT: For ISO strings without timezone information (e.g., "2025-05-02T11:50:39.654210"),
      // we need to explicitly specify that they should be interpreted as UTC
      // Without this, JavaScript would interpret the string as a local time, leading to incorrect timestamps
      // fromZonedTime() correctly converts from the specified timezone (UTC) to UTC
      date = fromZonedTime(dateString, TimeZone.UTC);
    }
  }
  // Check for YYYY-MM-DD HH:mm[:ss] UTC format (e.g., "2025-09-06 08:00 UTC" or "2025-09-15 14:30:45 UTC")
  // Case-insensitive to handle "UTC", "utc", "Utc", etc.
  else if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(?::\d{2})? UTC$/i.test(dateString)) {
    // Remove " UTC" (case insensitive) and replace space with "T", then add "Z" for UTC
    const cleanString = dateString.replace(/ UTC$/i, '').replace(' ', 'T');
    // Add seconds if missing
    const isoString = /:\d{2}:\d{2}$/.test(cleanString)
      ? cleanString + 'Z'
      : cleanString + ':00Z';
    date = new Date(isoString);
  }
  // Check for YYYY-MM-DD UTC format (date only, no time - e.g., "2025-09-15 UTC")
  else if (/^\d{4}-\d{2}-\d{2} UTC$/i.test(dateString)) {
    // Remove " UTC" and add default time
    const cleanString = dateString.replace(/ UTC$/i, '');
    date = new Date(`${cleanString}T00:00:00Z`);
  }
  // Check for YYYY/MM/DD HH:mm[:ss] UTC format (with forward slashes - e.g., "2025/12/03 21:00 UTC" or "2025/12/05 08:57:55 UTC")
  // Case-insensitive to handle "UTC", "utc", "Utc", etc.
  else if (
    /^\d{4}\/\d{2}\/\d{2} \d{2}:\d{2}(?::\d{2})? UTC$/i.test(dateString)
  ) {
    // Remove " UTC" (case insensitive), replace forward slashes with hyphens, and replace space with "T", then add "Z" for UTC
    const cleanString = dateString
      .replace(/ UTC$/i, '')
      .replace(/\//g, '-')
      .replace(' ', 'T');
    // Add seconds if missing
    const isoString = /:\d{2}:\d{2}$/.test(cleanString)
      ? cleanString + 'Z'
      : cleanString + ':00Z';
    date = new Date(isoString);
  }
  // Check for YYYY/MM/DD UTC format (date only, no time, with forward slashes - e.g., "2025/12/15 UTC")
  else if (/^\d{4}\/\d{2}\/\d{2} UTC$/i.test(dateString)) {
    // Remove " UTC", replace forward slashes with hyphens, and add default time
    const cleanString = dateString.replace(/ UTC$/i, '').replace(/\//g, '-');
    date = new Date(`${cleanString}T00:00:00Z`);
  }
  // Check for DD-MM-YYYY HH:mm[:ss] [UTC] format (e.g., "10-06-2026 07:28 UTC" or "09-06-2026 09:50:03 UTC")
  // 4-digit year variant of the machine-readable format
  else if (
    /^\d{2}-\d{2}-\d{4}[ T]\d{2}:\d{2}(?::\d{2})?( UTC)?$/i.test(dateString)
  ) {
    const cleanString = dateString.replace(/ UTC$/i, '').trim();
    const hasSeconds = /:\d{2}:\d{2}$/.test(cleanString);
    const format = hasSeconds ? 'dd-MM-yyyy HH:mm:ss' : 'dd-MM-yyyy HH:mm';
    const normalizedString = cleanString.replace('T', ' ');

    const parsed = tryParse(normalizedString, format);

    if (parsed) {
      date = fromZonedTime(parsed, 'UTC');
    } else {
      date = parseHumanDateString(dateString, timeZone);
    }
  }
  // Check for DD-MM-YY HH:mm[:ss] [UTC] format (e.g., "27-10-25 10:52" or "27-10-25 10:52:00 UTC")
  // This is a machine-readable format, not human-readable like "27 Oct 2025"
  else if (
    /^\d{2}-\d{2}-\d{2}[ T]\d{2}:\d{2}(?::\d{2})?( UTC)?$/i.test(dateString)
  ) {
    // Remove optional " UTC" suffix (case insensitive)
    const cleanString = dateString.replace(/ UTC$/i, '').trim();

    // Determine if we have seconds or not
    const hasSeconds = /:\d{2}:\d{2}$/.test(cleanString);
    const format = hasSeconds ? 'dd-MM-yy HH:mm:ss' : 'dd-MM-yy HH:mm';

    // Replace 'T' with space if present (to handle both separators)
    const normalizedString = cleanString.replace('T', ' ');

    const parsed = tryParse(normalizedString, format);

    if (parsed) {
      date = fromZonedTime(parsed, 'UTC');
    } else {
      // If parsing fails, fall through to human-readable format parser
      date = parseHumanDateString(dateString, timeZone);
    }
  }
  // Check for human-readable format
  else {
    date = parseHumanDateString(dateString, timeZone);
  }

  // Validate date object
  if (!validateDate(date)) {
    const error = new Error(
      `Date validation failed for "${dateString}": parsed date is invalid`,
    );
    Sentry.captureException(error, {
      tags: {
        function: 'parseDate',
        category: 'date_validation_error',
      },
      extra: {
        originalInput: dateString,
        timeZone: timeZone,
        parsedDate: date,
        dateType: typeof dateString,
      },
      level: 'warning',
      fingerprint: ['parseDate', 'validation_failed', String(dateString)],
    });

    return null;
  }

  return date;
}

export const convertFieldsToDates = (
  input: Record<string, any> | Record<string, any>[],
  fields: string[],
  timeZone?: string,
) => {
  const processField = (obj: Record<string, any>, fieldParts: any) => {
    if (fieldParts.length === 0) return;

    const currentPart = fieldParts.shift();
    if (!currentPart) return;

    const value = obj[currentPart];

    if (Array.isArray(value)) {
      value.forEach((item, index) => {
        if (typeof item === 'object') {
          processField(item, [...fieldParts]);
        } else {
          if (fieldParts.length === 0) {
            const arrayValue = value[index];
            if (
              arrayValue !== undefined &&
              arrayValue !== null &&
              (typeof arrayValue === 'string' || typeof arrayValue === 'number')
            ) {
              value[index] = parseDate(arrayValue, timeZone);
            }
          }
        }
      });
    } else if (typeof value === 'object' && value !== null) {
      processField(value, [...fieldParts]);
    } else if (fieldParts.length === 0 && currentPart in obj) {
      // Only parse if value is a string or number
      if (
        value !== undefined &&
        value !== null &&
        (typeof value === 'string' || typeof value === 'number')
      ) {
        obj[currentPart] = parseDate(value, timeZone);
      }
    }
  };

  if (Array.isArray(input)) {
    input.forEach((object) => convertFieldsToDates(object, fields));
    return;
  }

  const object = input;

  fields.forEach((field) => {
    const fieldParts = field.split('.');
    processField(object, fieldParts);
  });
};
